import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Download, Send, RefreshCw, Users, Eye, Loader2 } from "lucide-react";

export default function AdminSubscribers() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [program, setProgram] = useState("all");
  const [testEmail, setTestEmail] = useState("");
  const [lastResult, setLastResult] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers");
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadCSV = () => {
    window.open(`${API}/admin/subscribers/export.csv`, "_blank");
  };

  const sendDigest = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const body = { program };
      if (testEmail.trim()) body.test_recipient = testEmail.trim();
      const { data } = await api.post("/admin/email/send-digest", body);
      setLastResult(data);
      if (data.dry_run) {
        toast.message("Preview ready. Add RESEND_API_KEY to .env to actually send.");
      } else {
        toast.success(`Sent ${data.sent} of ${data.queued} digests.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send digest.");
    } finally {
      setSending(false);
    }
  };

  const filtered = (data?.subscribers || []).filter((s) =>
    !filter ? true : (s.email + " " + (s.name || "") + " " + (s.organization || "")).toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="bg-tcpi-bg min-h-screen" data-testid="admin-subscribers-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <div className="overline mb-3">Admin</div>
            <h1 className="text-4xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
              Email List & Weekly Digest
            </h1>
            <p className="text-[#8A8782] mt-2">
              Your contact list grows automatically. Send the weekly digest from here.
            </p>
          </div>
          <div className="flex gap-2">
            <Link to="/facilitator" className="btn-secondary text-sm" data-testid="back-to-facilitator">
              Participants
            </Link>
            <button onClick={load} className="btn-secondary text-sm flex items-center gap-2" data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-4 gap-6 mb-10">
          <Stat icon={Users} label="Total contacts" value={data?.total ?? "—"} />
          <Stat icon={Mail} label="Email subscribed" value={data?.subscribed ?? "—"} />
          <Stat icon={Users} label="Registered users" value={data?.registered_users ?? "—"} />
          <Stat icon={Mail} label="Newsletter only" value={data?.newsletter_only ?? "—"} />
        </div>

        {/* Digest control */}
        <div className="card-bento mb-10" data-testid="digest-controls">
          <div className="overline mb-4">Weekly Digest</div>
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm text-[#D1CFC9] mb-2">Program filter</label>
              <select
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                className="input-tcpi"
                data-testid="digest-program-select"
              >
                <option value="all">All participants</option>
                <option value="men">Men's program only</option>
                <option value="women">Women's program only</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm text-[#D1CFC9] mb-2">
                Test send (optional) — send only to this address
              </label>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-tcpi"
                data-testid="digest-test-email"
              />
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={sendDigest}
              disabled={sending}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
              data-testid="send-digest-btn"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Working…" : testEmail.trim() ? "Send Test Digest" : "Send Weekly Digest"}
            </button>
            <button onClick={downloadCSV} className="btn-secondary flex items-center gap-2" data-testid="export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          </div>

          {lastResult && (
            <div
              className={`mt-6 p-4 rounded-md border ${
                lastResult.dry_run
                  ? "border-[#C9A961]/40 bg-[#1A1816]"
                  : "border-green-500/40 bg-green-900/10"
              }`}
              data-testid="digest-result"
            >
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div className="text-sm text-[#F7F5F0]">
                  {lastResult.dry_run ? "DRY RUN — emails not actually sent" : "Digest sent"}
                </div>
                <div className="text-xs text-[#C9A961]">
                  Queued: {lastResult.queued} · Sent: {lastResult.sent} · Failed: {lastResult.failed}
                </div>
              </div>
              <p className="text-xs text-[#8A8782] mb-3">{lastResult.message}</p>
              {lastResult.sample_preview && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-[#C9A961] flex items-center gap-1 mb-2">
                    <Eye className="w-3 h-3" /> Preview sample email
                  </summary>
                  <div className="p-3 bg-[#0A0908] rounded">
                    <div className="text-[#8A8782]">To: {lastResult.sample_preview.to}</div>
                    <div className="text-[#8A8782] mb-2">Subject: {lastResult.sample_preview.subject}</div>
                    <iframe
                      title="email-preview"
                      srcDoc={lastResult.sample_preview.html}
                      className="w-full h-96 rounded border border-[#C9A961]/15 bg-white"
                    />
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Subscribers table */}
        <div className="card-bento p-0 overflow-hidden">
          <div className="px-6 pt-6 pb-4 flex items-center justify-between flex-wrap gap-3">
            <div className="overline">Contacts</div>
            <input
              placeholder="Filter by email, name, organization…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input-tcpi max-w-sm"
              data-testid="subscribers-filter"
            />
          </div>
          {loading && <div className="p-8 text-[#8A8782]">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-8 text-[#8A8782]">No contacts match.</div>
          )}
          <div className="max-h-[640px] overflow-y-auto">
            {filtered.map((s, i) => (
              <div
                key={s.email}
                className={`flex items-center justify-between px-6 py-4 ${
                  i !== 0 ? "border-t border-[#C9A961]/10" : ""
                }`}
                data-testid={`subscriber-row-${s.email}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[#F7F5F0] truncate">{s.email}</div>
                  <div className="text-xs text-[#8A8782] truncate">
                    {s.name || "—"} {s.organization ? `· ${s.organization}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-6 text-right text-xs">
                  <span className="overline">{s.source}</span>
                  {s.role && <span className="text-[#C9A961]">{s.role}</span>}
                  {s.program && <span className="text-[#8A8782]">{s.program}</span>}
                  <span
                    className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                      s.email_subscribed
                        ? "bg-[#C9A961]/15 text-[#C9A961]"
                        : "bg-red-900/30 text-red-400"
                    }`}
                  >
                    {s.email_subscribed ? "Subscribed" : "Unsub'd"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-bento">
      <Icon className="w-5 h-5 text-[#C9A961] mb-3" strokeWidth={1.5} />
      <div className="overline mb-1">{label}</div>
      <div className="text-4xl text-[#F7F5F0]" style={{ fontFamily: "Cormorant Garamond, serif" }}>
        {value}
      </div>
    </div>
  );
}
